import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type ReviewItem } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill } from "@/components/dash/ui";

const TRANSITIONS: Record<string, string[]> = {
  open: ["acknowledged", "in_progress", "resolved", "dismissed"],
  acknowledged: ["in_progress", "resolved", "dismissed", "open"],
  in_progress: ["acknowledged", "resolved", "dismissed"],
  resolved: ["open"],
  dismissed: ["open"],
};
const FILTERS = ["active", "open", "acknowledged", "in_progress", "resolved", "dismissed", "all"];

function Events({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["review-events", id], queryFn: () => dashApi.reviewEvents(id) });
  if (q.isLoading) return <p className="px-3 py-2 text-xs text-slate-500">Loading history…</p>;
  if (!q.data?.events.length) return <p className="px-3 py-2 text-xs text-slate-500">No history.</p>;
  return (
    <ul className="space-y-1.5 px-3 py-2">
      {q.data.events.map((e) => (
        <li key={e.id} className="flex items-start gap-2 text-[11px] text-slate-400">
          <span className="text-slate-600">{e.ts ? fmtDateTime(e.ts) : ""}</span>
          <span className="text-slate-300">{e.event_type}</span>
          {e.from_status && <span>{e.from_status} → {e.to_status}</span>}
          {e.note && <span className="text-slate-500">“{e.note}”</span>}
          {e.actor_email && <span className="ml-auto text-slate-600">{e.actor_email}</span>}
        </li>
      ))}
    </ul>
  );
}

function Row({ r, isAdmin }: { r: ReviewItem; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const mut = useMutation({
    mutationFn: (body: Parameters<typeof dashApi.updateReview>[1]) => dashApi.updateReview(r.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trust-reviews"] }),
  });
  const next = TRANSITIONS[r.status] ?? [];
  return (
    <>
      <tr className="text-slate-300">
        <td className="py-2">
          <button type="button" className="text-left hover:text-brand-300" onClick={() => setOpen((o) => !o)}>{r.title}</button>
          <div className="text-[11px] text-slate-600">{r.review_type.replace(/_/g, " ")}{r.source_code ? ` · ${r.source_code}` : ""}</div>
        </td>
        <td className="py-2"><StatusPill status={r.severity} /></td>
        <td className="py-2"><StatusPill status={r.status} label={r.status} /></td>
        <td className="py-2 text-[11px] text-slate-500">{r.confidence === null ? "—" : `${Math.round(r.confidence * 100)}%`}</td>
        <td className="py-2 text-slate-400">{r.owner ?? "—"}</td>
        <td className="py-2 text-[11px] text-slate-500">{fmtRelative(r.updated_at)}</td>
        <td className="py-2 text-right">
          {isAdmin && next.length > 0 && (
            <select value="" onChange={(e) => { if (e.target.value) mut.mutate({ status: e.target.value }); }}
              className="rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-[11px] text-slate-200 [color-scheme:dark]">
              <option value="">Move to…</option>
              {next.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="bg-ink-800/40">
          {r.detail && <p className="px-3 pt-2 text-xs text-slate-400">{r.detail}</p>}
          {r.entity_ref && <p className="px-3 text-[11px] text-slate-600">ref: {r.entity_ref}</p>}
          <Events id={r.id} />
        </td></tr>
      )}
    </>
  );
}

export default function ReviewQueuePage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [filter, setFilter] = useState("active");
  const q = useQuery({ queryKey: ["trust-reviews", filter], queryFn: () => dashApi.reviewItems(filter === "all" ? undefined : filter) });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review Queue"
        description="IRONDash-only review of possible matches, suspicious duplicates, high-value unmatched records, conflicts and double-counting risks. Review actions only change IRONDash metadata — no source record is ever modified."
      />
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`rounded-md px-2.5 py-1 text-xs ${filter === f ? "bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40" : "bg-ink-800 text-slate-400 hover:bg-ink-700"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>
      <Panel title={`Review items (${q.data?.items.length ?? 0})`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.items.length ? (
          <EmptyState>No review items in this view. Raise one from the Reconciliation Centre.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="pb-2 font-medium">Item</th><th className="pb-2 font-medium">Severity</th>
              <th className="pb-2 font-medium">Status</th><th className="pb-2 font-medium">Confidence</th>
              <th className="pb-2 font-medium">Owner</th><th className="pb-2 font-medium">Updated</th><th className="pb-2" />
            </tr></thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data.items.map((r) => <Row key={r.id} r={r} isAdmin={isAdmin} />)}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
